import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSearch,
  FaDownload,
  FaPlus,
  FaEdit,
  FaUser,
  FaSync,
  FaTrash,
  FaCalendarAlt,
  FaChevronUp,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaTachometerAlt,
  FaList,
  FaTable,
  FaEye,
  FaPaperPlane,
  FaHandshake,
  FaFileInvoice,
  FaExclamationTriangle,
} from "react-icons/fa";
import { 
 ArrowDownTrayIcon, TrashIcon, PlusCircleIcon
} from '@heroicons/react/24/outline';
import { TbGps } from "react-icons/tb";
import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import RentalDetailsModal from "../Modals/RentalDetailsModal";
import AssignPartnerModal from "../Modals/AssignPartnerModal";
import CombinedRentalHandoverModal from "../../RentalModal/CombinedRentalHandoverModal";
import {
  ITEMS_PER_PAGE,
  RENTALS_PER_PAGE,
} from "../../AdminDashBoard/data/perpage";
import { rentals as RentalsApi, handoverService, cars } from "../../../services/api";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

// Utility functions for calculating rental duration and additional options pricing
const calculateRentalDays = (pickupDateTime, returnDateTime) => {
  const pickup = new Date(pickupDateTime);
  const returnDate = new Date(returnDateTime);
  const diffInMs = returnDate - pickup;
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24)); // Round up to ensure minimum 1 day
  return Math.max(1, diffInDays); // Minimum 1 day
};

const calculateAdditionalOptionsPrice = (additionalOptions, rentalDays) => {
  if (!additionalOptions || additionalOptions.length === 0) return 0;
  return additionalOptions.reduce(
    (sum, option) => sum + option.price * rentalDays,
    0
  );
};

function Rentals({
  // Server-side rentals hook
  serverSideRentals,
  // Legacy props for backward compatibility
  handlePaymentStatusUpdate,
  getDailyTurnover,
  getTomorrowTurnover,
  setTurnoverPage,
  turnoverPage,
  deletingRentalId,
  setIsRentalModalOpen,
  fetchData,
  updatingRentalId,
  updatingPaymentId,
  setSelectedRental,
  setIsEditRentalModalOpen,
  handleRentalStatusUpdate,
  handleRentalDelete,
  formatDateTime,
  expandedCustomer,
  setExpandedCustomer,
  onSetIsToday,
  onSetIsTomorrow,
}) {
  // Extract server-side rentals data and functions
  const {
    rentalsData: filteredRentals,
    isLoading: rentalsLoading,
    error: rentalsError,
    currentPage,
    totalPages: totalRentalPages,
    totalCount,
    hasNextPage,
    hasPrevPage,
    goToPage,
    goToNextPage,
    goToPrevPage,
    setCurrentPage,
    search: rentalSearch,
    setSearch: setRentalSearch,
    statusFilter: rentalStatusFilter,
    setStatusFilter: setRentalStatusFilter,
    partnerFilter,
    setPartnerFilter,
    dateFilter,
    setDateFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    paymentStatusFilter,
    setPaymentStatusFilter,
    clearFilters,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    refetch
  } = serverSideRentals || {};

  // Add a new state to manage the active tab
  const [activeTab, setActiveTab] = useState("compact-view");
  // Add a state for the rental details modal
  const [selectedModalRental, setSelectedModalRental] = useState(null);
  const [isRentalDetailsModalOpen, setIsRentalDetailsModalOpen] =
    useState(false);  // Add a state to track if filters are expanded or collapsed
  const [isFilterExpanded, setIsFilterExpanded] = useState(true);
  // Add states for collapsible sections
  const [isQuickCreateExpanded, setIsQuickCreateExpanded] = useState(false);
  const [isCompactViewExpanded, setIsCompactViewExpanded] = useState(true);
  const [isPricingExpanded, setIsPricingExpanded] = useState(false);
  const [expandedRentals, setExpandedRentals] = useState(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  const [rentals, setRentals] = useState([]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle rental expansion for mobile view
  const toggleRentalExpansion = (rentalId) => {
    const newExpanded = new Set(expandedRentals);
    if (newExpanded.has(rentalId)) {
      newExpanded.delete(rentalId);
    } else {
      newExpanded.add(rentalId);
    }
    setExpandedRentals(newExpanded);
  };

  // States for AADE and Oxygen submissions
  const [submittingAADE, setSubmittingAADE] = useState(null);
  const [submittingOxygen, setSubmittingOxygen] = useState(null);  
  const [handoversData, setHandoversData] = useState({});
  const [loadingHandovers, setLoadingHandovers] = useState({});
  const [isCombinedModalOpen, setIsCombinedModalOpen] = useState(false);  // States for assign partner functionality
  const [isAssignPartnerModalOpen, setIsAssignPartnerModalOpen] = useState(false);
  const [selectedRentalForPartner, setSelectedRentalForPartner] = useState(null);
  const [assigningPartner, setAssigningPartner] = useState(false);

      // Quick Rental Creation state
      const [availableCars, setAvailableCars] = useState([]);
      const [availableLicensePlates, setAvailableLicensePlates] = useState([]);
      const [guestCounter, setGuestCounter] = useState(1);
      const [creatingRental, setCreatingRental] = useState(false);
      const [pickupLocation, setPickupLocation] = useState('Mykonos Airport');
      const [returnLocation, setReturnLocation] = useState('Mykonos Airport');
      const [quickRentalRows, setQuickRentalRows] = useState([]);  // Initialize as empty, we'll populate after getting the guest counter
      const [selectedInvoiceKind, setSelectedInvoiceKind] = useState(1);
      // Function to create an empty quick rental row
      function createEmptyQuickRentalRow(guestNum) {
          const today = new Date();
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          return {
              id: Math.random().toString(36).substring(2, 9),
              licensePlate: '',
              car: null,
              customer: {
                  firstName: `Guest`,
                  lastName: `${guestNum}`,
                  email: 'niotisan@gmail.com',
                  phoneNumber: '+306941419012',
                  age: '26+'
              },
              pickupDateTime: today,
              returnDateTime: tomorrow,
              submitToAade: true
          };
      }

  const handleOpenRentalDetails = (rental) => {
    setSelectedModalRental(rental);
    setIsRentalDetailsModalOpen(true);
  };

  // Function to check if a rental has a completed pickup handover
  const hasCompletedPickupHandover = (rentalId) => {
    const handovers = handoversData[rentalId] || [];
    return handovers.some(
      (handover) =>
        handover.handoverType === "pickup" && handover.status === "completed"
    );
  };

  // Function to load handovers for a rental
  const loadHandovers = async (rentalId) => {
    if (loadingHandovers[rentalId] || handoversData[rentalId]) {
      return; // Already loading or loaded
    }

    setLoadingHandovers((prev) => ({ ...prev, [rentalId]: true }));

    try {
      const response = await handoverService.getHandoversByRental(rentalId);
      setHandoversData((prev) => ({ ...prev, [rentalId]: response.data }));
    } catch (error) {
      console.error(`Error loading handovers for rental ${rentalId}:`, error);
      setHandoversData((prev) => ({ ...prev, [rentalId]: [] }));
    } finally {
      setLoadingHandovers((prev) => ({ ...prev, [rentalId]: false }));
    }  };

  // Load handovers for visible rentals
  useEffect(() => {
    if (filteredRentals && filteredRentals.length > 0) {
      filteredRentals.forEach((rental) => {
        loadHandovers(rental._id);
      });
    }
                  {console.log(filteredRentals)}

  }, [filteredRentals]);

  // Function to handle AADE submission
  const handleAADESubmission = async (rental) => {
    if (!hasCompletedPickupHandover(rental._id)) {
      alert(
        "AADE submission is only available after a pickup handover is completed."
      );
      return;
    }

    if (!rental.licensePlate) {
      alert("Cannot submit to AADE: License plate is required.");
      return;
    }

    if (rental.aade?.submitted) {
      alert("This rental has already been submitted to AADE.");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to submit rental for ${rental.customer.firstName} ${rental.customer.lastName} to AADE?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    setSubmittingAADE(rental._id);

    try {
      const response = await RentalsApi.submitAADE(rental._id);
      alert(`AADE submission successful! DCLID: ${response.data.dclId}`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error("AADE submission error:", error);
      alert(`AADE submission failed: ${error.message}`);
    } finally {
      setSubmittingAADE(null);
    }
  };

  // Function to handle Oxygen submission
  const handleOxygenSubmission = async (rental) => {
    if (!hasCompletedPickupHandover(rental._id)) {
      alert(
        "Oxygen submission is only available after a pickup handover is completed."
      );
      return;
    }

    // if (!rental.invoiceData?.vatNumber) {
    //   alert('Cannot submit to Oxygen: Valid invoice data with VAT number is required.');
    //   return;
    // }

    if (rental.oxygenInvoiceId) {
      alert("This rental already has an Oxygen invoice.");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to create an Oxygen invoice for ${rental.customer.firstName} ${rental.customer.lastName}?\n\nThis will generate an invoice document.`
    );

    if (!confirmed) return;

    setSubmittingOxygen(rental._id);

    try {
      const response = await RentalsApi.submitOxygen(rental._id);
      console.log("Oxygen submission response:", response);
      alert(`Oxygen invoice created successfully!`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Oxygen submission error:", error);
      alert(`Oxygen submission failed: ${error.message}`);
    } finally {
      setSubmittingOxygen(null);
    }
  };

  // Handle license plate selection
    const handleLicensePlateChange = (rowIndex, licensePlateInfo) => {
        const updatedRows = [...quickRentalRows];
        
        // Find the car that corresponds to this license plate
        if (licensePlateInfo) {
            // Update row with selected license plate info
            updatedRows[rowIndex] = {
                ...updatedRows[rowIndex],
                licensePlate: licensePlateInfo.plate,
                vehicleId: licensePlateInfo.vehicleId,
                car: licensePlateInfo.carId,
                carName: licensePlateInfo.carName
            };
        } else {
            // Clear license plate and car info
            updatedRows[rowIndex] = {
                ...updatedRows[rowIndex],
                licensePlate: '',
                vehicleId: null,
                car: null,
                carName: ''
            };
        }
        
        setQuickRentalRows(updatedRows);
    };

    // Handle date time changes
    const handleDateChange = (rowIndex, fieldName, newDate) => {
        const updatedRows = [...quickRentalRows];
        updatedRows[rowIndex] = {
            ...updatedRows[rowIndex],
            [fieldName]: newDate
        };
        setQuickRentalRows(updatedRows);
    };

    // Add new quick rental row
    const addQuickRentalRow = () => {
        const newCounter = guestCounter + quickRentalRows.length;
        
        const newRow = createEmptyQuickRentalRow(newCounter);
        
        setQuickRentalRows([...quickRentalRows, newRow]);
    };

    // Delete quick rental row
    const deleteQuickRentalRow = (rowIndex) => {
        if (quickRentalRows.length === 1) {
            // Don't delete the last row, just clear it
            const clearedRow = createEmptyQuickRentalRow();
            setQuickRentalRows([clearedRow]);
        } else {
            const updatedRows = quickRentalRows.filter((_, index) => index !== rowIndex);
            setQuickRentalRows(updatedRows);
        }
    };


        // Create rentals from quick rental rows
      const createQuickRentals = async () => {
          setCreatingRental(true);
          let successCount = 0;
          let failureCount = 0;
          let currentGuestCounter = guestCounter;
          
          for (const row of quickRentalRows) {
              if (!row.licensePlate || !row.car) {
                  toast.error(`Cannot create rental: ${!row.licensePlate ? 'License plate' : 'Car'} is required`, {
                      position: "top-right",
                      autoClose: 3000
                  });
                  failureCount++;
                  continue; // Skip rows without license plate or car
              }
              
              try {
                  // Calculate days difference
                  const diffInMilliseconds = row.returnDateTime - row.pickupDateTime;
                  const diffInHours = diffInMilliseconds / (1000 * 60 * 60);
                  const days = Math.ceil(diffInHours / 24);
                  
                  // Find the car info by matching ID
                  const carInfo = availableCars.find(car => car._id === row.car);
                  
                  // Find vehicle instance by matching vehicle ID
                  const vehicleInstance = row.vehicleId ? 
                      availableCars.flatMap(car => car.vehicleInstances || [])
                      .find(vehicle => vehicle._id === row.vehicleId) : null;
                  
                  if (!carInfo) {
                      console.error('Car not found for row:', row);
                      failureCount++;
                      continue;
                  }
                  
                  // Create customer with incrementing guest number
                  const customer = {
                      ...row.customer,
                      firstName: 'Guest',
                      lastName: `${currentGuestCounter}`
                  };
                  
                  // Create rental data
                  const rentalData = {
                      car: carInfo.stringId,
                      vehicleInstanceId: row.vehicleId || null,
                      licensePlate: row.licensePlate,
                      customer: customer,
                      pickupLocation: pickupLocation,
                      returnLocation: returnLocation,
                      pickupDateTime: row.pickupDateTime.toISOString(),
                      returnDateTime: row.returnDateTime.toISOString(),
                      additionalOptions: {
                          childSeat: false,
                          babySeat: false,
                          gps: false
                      },
                      basePrice: carInfo.basePrice || 25,
                      customPricePerDay: carInfo.basePrice || 25,
                      totalPrice: (carInfo.basePrice || 25) * days,
                      priceBreakdown: [
                          {
                              startDate: row.pickupDateTime.toISOString(),
                              endDate: row.returnDateTime.toISOString(),
                              pricePerDay: carInfo.basePrice || 25,
                              numberOfDays: days,
                              totalForPeriod: (carInfo.basePrice || 25) * days
                          }
                      ],
                      additionalNotes: `Quick rental created for Guest - ${currentGuestCounter}`,
                      status: 'confirmed' // Set as confirmed so it can be submitted to AADE
                  };
                  
                  console.log('Creating rental with data:', rentalData);
                  
                  // Create the rental
                  const response = await RentalsApi.create(rentalData);
                  console.log('Created rental:', response.data);
                  
                  // Submit to AADE if requested
                  if (row.submitToAade) {
                      try {
                          if (!row.licensePlate) {
                              toast.error('Cannot submit to AADE: License plate is mandatory', {
                                  position: "top-right",
                                  autoClose: 3000
                              });
                          } else {
                              const aadeResponse = await axios.post(`https://rent-server.create-site.gr/api/aade/rentals/${response.data._id}/submit`);
                              console.log('AADE submission response:', aadeResponse.data);
                          }
                      } catch (aadeError) {
                          console.error('Error submitting to AADE:', aadeError);
                          toast.error(aadeError.response?.data?.message || 'Error submitting to AADE: License plate may be missing or invalid', {
                              position: "top-right",
                              autoClose: 3000
                          });
                          // We still count this as success since the rental was created
                      }
                  }
                  
                  successCount++;
                  currentGuestCounter++; // Increment guest number for next rental
              } catch (error) {
                  console.error('Error creating rental:', error);
                  toast.error(`Error creating rental: ${error.message || 'Unknown error'}`, {
                      position: "top-right",
                      autoClose: 3000
                  });
                  failureCount++;
              }
          }
          
          // Refresh rentals list
          try {
              const response = await RentalsApi.getAll();
              setRentals(response.data);
              
              // Update guest counter to the next available number
              setGuestCounter(currentGuestCounter);
              
              // Refresh available license plates after creating rentals
              const carsResponse = await cars.getAll();
              
              // Extract license plates from vehicle instances
              const licensePlatesArray = [];
              carsResponse.data.forEach(car => {
                  if (car.vehicleInstances && car.vehicleInstances.length > 0) {
                      car.vehicleInstances.forEach(vehicle => {
                          if (vehicle.licensePlate && vehicle.status === 'available') {
                              licensePlatesArray.push({
                                  plate: vehicle.licensePlate,
                                  vehicleId: vehicle._id,
                                  carId: car._id,
                                  carName: `${car.make} ${car.model}`,
                                  plateWithCar: `${vehicle.licensePlate} (${car.make} ${car.model})`
                              });
                          }
                      });
                  }
              });
              
              setAvailableLicensePlates(licensePlatesArray);
          } catch (error) {
              console.error('Error refreshing rentals:', error);
          }
          
          setCreatingRental(false);
          toast.info(`Created ${successCount} rentals. ${failureCount > 0 ? `Failed: ${failureCount}` : ''}`, {
              position: "top-right",
              autoClose: 3000
          });
          
          // Reset the quick rental rows with the updated guest counter
          const newCounter = currentGuestCounter;
          console.log('Setting new guest counter to:', newCounter);
          setGuestCounter(newCounter);
          const newRow = createEmptyQuickRentalRow(newCounter);
          setQuickRentalRows([newRow]);
      };

  // Function to handle opening assign partner modal
  const handleOpenAssignPartner = (rental) => {
    setSelectedRentalForPartner(rental);
    setIsAssignPartnerModalOpen(true);
  };

  // Function to handle partner assignment
  const handleAssignPartner = async (rentalId, partnerId) => {
    try {
      setAssigningPartner(true);
      const response = await RentalsApi.assignToPartner(rentalId, partnerId);
      
      // Show success message
      alert("Rental successfully assigned to partner!");
      // fetchData(); // Refresh data
      // Refresh the rentals data using server-side rentals refetch
      refetch();
      
      // Close modal
      setIsAssignPartnerModalOpen(false);
      setSelectedRentalForPartner(null);
      
    } catch (error) {
      console.error("Error assigning partner:", error);
      alert(`Failed to assign partner: ${error.message}`);
    } finally {
      setAssigningPartner(false);
    }
  };

  // Function to render handover information
  const renderHandoverInfo = (rental) => {
    const handovers = handoversData[rental._id] || [];
    const isLoading = loadingHandovers[rental._id];

    if (isLoading) {
      return (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center text-sm text-gray-500">
            <FaSync className="w-4 h-4 mr-2 animate-spin" />
            Loading handovers...
          </div>
        </div>
      );
    }

    if (handovers.length === 0) {
      return (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-500">No handovers recorded yet</div>
        </div>
      );
    }

    return (
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
          <FaHandshake className="w-4 h-4 mr-2" />
          Vehicle Handovers ({handovers.length})
        </h5>
        <div className="space-y-2">
          {handovers.map((handover, index) => (
            <div
              key={handover._id}
              className="flex items-center justify-between text-xs bg-white p-2 rounded border"
            >
              <div className="flex items-center space-x-2">
                <span
                  className={`px-2 py-1 rounded-full ${
                    handover.handoverType === "pickup"
                      ? "bg-green-100 text-green-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {handover.handoverType === "pickup" ? "Pickup" : "Return"}
                </span>
                <span className="text-gray-600">
                  {moment(handover.createdAt).format("DD/MM/YY HH:mm")}
                </span>
                <span
                  className={`px-2 py-1 rounded ${
                    handover.status === "completed"
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {handover.status}
                </span>
              </div>
              <div className="text-gray-500">
                {handover.mileage}km, {handover.fuelLevel}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Function to render submission buttons
  const renderSubmissionButtons = (rental) => {
    const hasPickupHandover = hasCompletedPickupHandover(rental._id);

    if (!hasPickupHandover) {
      return null; // Don't show buttons if no pickup handover completed
    }

    return (
      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <h5 className="text-sm font-medium text-blue-900 mb-2">
          Manual Submissions
        </h5>
        <div className="flex gap-2">
          {/* AADE Submission Button */}
          {!rental.aade?.submitted ? (
            <button
              onClick={() => handleAADESubmission(rental)}
              disabled={submittingAADE === rental._id || !rental.licensePlate}
              className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              title={
                !rental.licensePlate
                  ? "License plate required for AADE submission"
                  : "Submit to AADE"
              }
            >
              {submittingAADE === rental._id ? (
                <FaSync className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <FaPaperPlane className="w-3 h-3 mr-1" />
              )}
              Submit AADE
            </button>
          ) : (
            <div className="px-3 py-1.5 bg-green-100 text-green-800 text-xs rounded-md flex items-center">
              <span className="w-3 h-3 mr-1">✓</span>
              AADE Submitted
            </div>
          )}

          {/* Oxygen Submission Button */}
          {!rental.oxygenInvoiceId ? (
            <button
              onClick={() => handleOxygenSubmission(rental)}
              disabled={
                submittingOxygen === rental._id ||
                !rental.invoiceData?.vatNumber
              }
              className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              title={
                !rental.invoiceData?.vatNumber
                  ? "Valid invoice data required for Oxygen submission"
                  : "Create Oxygen invoice"
              }
            >
              {submittingOxygen === rental._id ? (
                <FaSync className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <FaFileInvoice className="w-3 h-3 mr-1" />
              )}
              Create Invoice
            </button>
          ) : (
            <div className="px-3 py-1.5 bg-green-100 text-green-800 text-xs rounded-md flex items-center">
              <span className="w-3 h-3 mr-1">✓</span>
              Invoice Created
            </div>
          )}
        </div>
      </div>
    );
  };

      const toggleSubmitToAade = (rowIndex) => {
        const updatedRows = [...quickRentalRows];
        updatedRows[rowIndex] = {
            ...updatedRows[rowIndex],
            submitToAade: !updatedRows[rowIndex].submitToAade
        };
        setQuickRentalRows(updatedRows);
    };
  return (
    <>
      {/* Collapsible Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer"
          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
        >
          <h2 className="text-lg font-semibold text-gray-800">Rentals Management</h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              {filteredRentals?.length || 0} rentals
            </span>
            <svg 
              className={`w-5 h-5 transition-transform ${isFilterExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        
        {isFilterExpanded && (
          <div className="border-t p-4">
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-2 lg:grid-cols-4 gap-2'}`}>
              <button
                onClick={() => setActiveTab("turnovers")}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${
                  activeTab === "turnovers"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FaTachometerAlt className="w-4 h-4" />
                <span>Turnovers</span>
              </button>

              <button
                onClick={() => setActiveTab("rental-list")}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${
                  activeTab === "rental-list"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FaList className="w-4 h-4" />
                <span>Rental List</span>
              </button>

              <button
                onClick={() => setActiveTab("compact-view")}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${
                  activeTab === "compact-view"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FaTable className="w-4 h-4" />
                <span>Compact View</span>
              </button>
              
              <button
                onClick={() => setActiveTab("quick-rental-creator")}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${
                  activeTab === "quick-rental-creator"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FaPlus className="w-4 h-4" />
                <span>Quick Create</span>
              </button>
            </div>
          </div>
        )}
      </div>      {/* Turnovers Tab Content */}
      {activeTab === "turnovers" && (
        <div className="space-y-4">
          {/* Today's Turnover Summary - Collapsible */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div 
              className="flex items-center justify-between p-4 cursor-pointer"
              onClick={() => setIsCompactViewExpanded(!isCompactViewExpanded)}
            >
              <h3 className="text-lg font-bold text-gray-900">
                Today's Turnover - {moment().format(isMobile ? "MMM D" : "dddd, MMMM D, YYYY")}
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetIsToday();
                  }}
                  className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <FaDownload className="w-3 h-3" />
                  {!isMobile && <span>Download</span>}
                </button>
                <svg 
                  className={`w-5 h-5 transition-transform ${isCompactViewExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            
            {isCompactViewExpanded && (
              <div className="border-t p-4">
                <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-6`}>
              {/* Pickups */}
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-green-800 mb-3">
                  Vehicles to Hand Over
                </h4>
                <div className="space-y-3">
                  {getDailyTurnover().pickups.length === 0 ? (
                    <p className="text-sm text-gray-600">
                      No vehicle handovers scheduled for today.
                    </p>
                  ) : (
                    getDailyTurnover().pickups.map((rental) => (
                      <div
                        key={rental._id}
                        className="bg-white p-3 rounded-lg border border-green-200"
                      >
                        <div className="flex justify-between items-start truncate">
                          <div>
                            <p className="font-medium text-gray-900">
                              {rental.carName}
                            </p>
                            <p className="text-sm text-gray-600">
                              Time:{" "}
                              {moment(rental.pickupDateTime).format("HH:mm")}
                            </p>
                            <p className="text-sm text-gray-600">
                              Location: {rental.pickupLocation}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">
                              {rental.customer.firstName}{" "}
                              {rental.customer.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {rental.customer.phoneNumber}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )
                  }
                </div>
                {/* Pagination for Pickups */}
                {getDailyTurnover().totalPickups > ITEMS_PER_PAGE && (
                  <div className="flex justify-between items-center mt-4">
                    <button
                      onClick={() =>
                        setTurnoverPage((prev) => ({
                          ...prev,
                          pickups: Math.max(prev.pickups - 1, 1),
                        }))
                      }
                      disabled={turnoverPage.pickups === 1}
                      className="px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {turnoverPage.pickups} of{" "}
                      {Math.ceil(
                        getDailyTurnover().totalPickups / ITEMS_PER_PAGE
                      )}
                    </span>
                    <button
                      onClick={() =>
                        setTurnoverPage((prev) => ({
                          ...prev,
                          pickups: Math.min(
                            prev.pickups + 1,
                            Math.ceil(
                              getDailyTurnover().totalPickups / ITEMS_PER_PAGE
                            )
                          ),
                        }))
                      }
                      disabled={
                        turnoverPage.pickups ===
                        Math.ceil(
                          getDailyTurnover().totalPickups / ITEMS_PER_PAGE
                        )
                      }
                      className="px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>

              {/* Returns */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-blue-800 mb-3">
                  Expected Returns
                </h4>
                <div className="space-y-3">
                  {getDailyTurnover().returns.length === 0 ? (
                    <p className="text-sm text-gray-600">
                      No vehicle returns scheduled for today.
                    </p>
                  ) : (
                    getDailyTurnover().returns.map((rental) => (
                      <div
                        key={rental._id}
                        className="bg-white p-3 rounded-lg border border-blue-200"
                      >
                        <div className="flex justify-between items-start truncate">
                          <div>
                            <p className="font-medium text-gray-900">
                              {rental.carName}
                            </p>
                            <p className="text-sm text-gray-600">
                              Time:{" "}
                              {moment(rental.returnDateTime).format("HH:mm")}
                            </p>
                            <p className="text-sm text-gray-600">
                              Location: {rental.returnLocation}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">
                              {rental.customer.firstName}{" "}
                              {rental.customer.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {rental.customer.phoneNumber}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {/* Pagination for Returns */}
                {getDailyTurnover().totalReturns > ITEMS_PER_PAGE && (
                  <div className="flex justify-between items-center mt-4">
                    <button
                      onClick={() =>
                        setTurnoverPage((prev) => ({
                          ...prev,
                          returns: Math.max(prev.returns - 1, 1),
                        }))
                      }
                      disabled={turnoverPage.returns === 1}
                      className="px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {turnoverPage.returns} of{" "}
                      {Math.ceil(
                        getDailyTurnover().totalReturns / ITEMS_PER_PAGE
                      )}
                    </span>
                    <button
                      onClick={() =>
                        setTurnoverPage((prev) => ({
                          ...prev,
                          returns: Math.min(
                            prev.returns + 1,
                            Math.ceil(
                              getDailyTurnover().totalReturns / ITEMS_PER_PAGE
                            )
                          ),
                        }))
                      }
                      disabled={
                        turnoverPage.returns ===
                        Math.ceil(
                          getDailyTurnover().totalReturns / ITEMS_PER_PAGE
                        )
                      }
                      className="px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}              </div>
            </div>
              </div>
            )}
          </div>

          {/* Tomorrow Turnover Summary - Collapsible */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div 
              className="flex items-center justify-between p-4 cursor-pointer"
              onClick={() => setIsQuickCreateExpanded(!isQuickCreateExpanded)}
            >
              <h3 className="text-lg font-bold text-gray-900">
                Tomorrow's Turnover - {moment().add(1, "days").format(isMobile ? "MMM D" : "dddd, MMMM D, YYYY")}
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetIsTomorrow();
                  }}
                  className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <FaDownload className="w-3 h-3" />
                  {!isMobile && <span>Download</span>}
                </button>
                <svg 
                  className={`w-5 h-5 transition-transform ${isQuickCreateExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
              {isQuickCreateExpanded && (
              <div className="border-t p-4">
                <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-6`}>
              {/* Tomorrow's Pickups */}
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-green-800 mb-3">
                  Vehicles to Hand Over
                </h4>
                <div className="space-y-3">
                  {getTomorrowTurnover().pickups.length === 0 ? (
                    <p className="text-sm text-gray-600">
                      No vehicle handovers scheduled for tomorrow.
                    </p>
                  ) : (
                    getTomorrowTurnover().pickups.map((rental) => (
                      <div
                        key={rental._id}
                        className="bg-white p-3 rounded-lg border border-green-200 truncate"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-900">
                              {rental.carName}
                            </p>
                            <p className="text-sm text-gray-600">
                              Time:{" "}
                              {moment(rental.pickupDateTime).format("HH:mm")}
                            </p>
                            <p className="text-sm text-gray-600">
                              Location: {rental.pickupLocation}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">
                              {rental.customer.firstName}{" "}
                              {rental.customer.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {rental.customer.phoneNumber}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {/* Pagination for Tomorrow's Pickups */}
                {getTomorrowTurnover().totalPickups > ITEMS_PER_PAGE && (
                  <div className="flex justify-between items-center mt-4">
                    <button
                      onClick={() =>
                        setTurnoverPage((prev) => ({
                          ...prev,
                          pickups: Math.max(prev.pickups - 1, 1),
                        }))
                      }
                      disabled={turnoverPage.pickups === 1}
                      className="px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {turnoverPage.pickups} of{" "}
                      {Math.ceil(
                        getTomorrowTurnover().totalPickups / ITEMS_PER_PAGE
                      )}
                    </span>
                    <button
                      onClick={() =>
                        setTurnoverPage((prev) => ({
                          ...prev,
                          pickups: Math.min(
                            prev.pickups + 1,
                            Math.ceil(
                              getTomorrowTurnover().totalPickups /
                                ITEMS_PER_PAGE
                            )
                          ),
                        }))
                      }
                      disabled={
                        turnoverPage.pickups ===
                        Math.ceil(
                          getTomorrowTurnover().totalPickups / ITEMS_PER_PAGE
                        )
                      }
                      className="px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
              {/* Tomorrow's Returns */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-blue-800 mb-3">
                  Expected Returns
                </h4>
                <div className="space-y-3">
                  {getTomorrowTurnover().returns.length === 0 ? (
                    <p className="text-sm text-gray-600">
                      No vehicle returns scheduled for tomorrow.
                    </p>
                  ) : (
                    getTomorrowTurnover().returns.map((rental) => (
                      <div
                        key={rental._id}
                        className="bg-white p-3 rounded-lg border border-blue-200 truncate"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-900">
                              {rental.carName}
                            </p>
                            <p className="text-sm text-gray-600">
                              Time:{" "}
                              {moment(rental.returnDateTime).format("HH:mm")}
                            </p>
                            <p className="text-sm text-gray-600">
                              Location: {rental.returnLocation}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">
                              {rental.customer.firstName}{" "}
                              {rental.customer.lastName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {rental.customer.phoneNumber}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {/* Pagination for Tomorrow's Returns */}
                {getTomorrowTurnover().totalReturns > ITEMS_PER_PAGE && (
                  <div className="flex justify-between items-center mt-4">
                    <button
                      onClick={() =>
                        setTurnoverPage((prev) => ({
                          ...prev,
                          returns: Math.max(prev.returns - 1, 1),
                        }))
                      }
                      disabled={turnoverPage.returns === 1}
                      className="px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {turnoverPage.returns} of{" "}
                      {Math.ceil(
                        getTomorrowTurnover().totalReturns / ITEMS_PER_PAGE
                      )}
                    </span>
                    <button
                      onClick={() =>
                        setTurnoverPage((prev) => ({
                          ...prev,
                          returns: Math.min(
                            prev.returns + 1,
                            Math.ceil(
                              getTomorrowTurnover().totalReturns /
                                ITEMS_PER_PAGE
                            )
                          ),
                        }))
                      }
                      disabled={
                        turnoverPage.returns ===
                        Math.ceil(
                          getTomorrowTurnover().totalReturns / ITEMS_PER_PAGE
                        )
                      }
                      className="px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>                )}
              </div>
            </div>
                </div>
              )}
          </div>
        </div>
      )}
      
      {/* Loading Indicator */}
      {rentalsLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-lg font-medium text-gray-700">Loading rentals...</p>
            <p className="text-sm text-gray-500">Please wait while we fetch your data</p>
          </div>
        </div>
      )}
        {/* Rental List Tab Content */}
      {activeTab === "rental-list" && (
        <div className="space-y-4">
          {/* Search and Filter Section - Collapsible */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div 
              className="flex items-center justify-between p-4 cursor-pointer"
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            >
              <h3 className="text-lg font-semibold text-gray-800">Search & Filters</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {filteredRentals?.length || 0} results
                </span>
                <svg 
                  className={`w-5 h-5 transition-transform ${isFilterExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            
            {isFilterExpanded && (
              <div className="border-t p-4">
                {/* Basic Search and Action Buttons - Always Visible */}
                <div className={`flex ${isMobile ? 'flex-col' : 'flex-col md:flex-row'} gap-4 items-center justify-between mb-4`}>
                  <div className="relative w-full md:w-1/2">
                    <input
                      type="text"
                      placeholder="Search by name, car, location..."
                      value={rentalSearch}
                      onChange={(e) => setRentalSearch(e.target.value)}
                      disabled={rentalsLoading}
                      className={`w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 ${
                        rentalsLoading ? "bg-gray-100 text-gray-500" : ""
                      }`}
                    />
                    {rentalsLoading ? (
                      <div className="absolute left-3 top-3 animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    ) : (
                      <FaSearch className="absolute left-3 top-3 text-gray-400" />
                    )}
                  </div>

                  <div className={`flex ${isMobile ? 'flex-col w-full' : 'gap-2'} w-full md:w-auto`}>
                    <motion.button
                      onClick={() => setIsRentalModalOpen(true)}
                      className={`px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-300 flex items-center justify-center ${isMobile ? 'mb-2' : ''}`}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <FaPlus className="w-4 h-4 mr-2" />
                      <span>Create Rental</span>
                    </motion.button>
                    <motion.button
                      onClick={() => setIsCombinedModalOpen(true)}
                      className={`px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-colors duration-300 flex items-center justify-center ${isMobile ? 'mb-2' : ''}`}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <FaPlus className="w-4 h-4 mr-2" />
                      <span>Create Combined</span>
                    </motion.button>                    <button
                      onClick={fetchData}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                    >
                      <FaSync className="w-4 h-4" />
                      {!isMobile && <span className="ml-2">Refresh</span>}
                    </button>
                  </div>
                </div>

                {/* Advanced Filter Section */}
                <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-4'} gap-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200`}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rental Status
                      </label>
                      <select
                        value={rentalStatusFilter}
                        onChange={(e) => setRentalStatusFilter(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Partner Assignment
                      </label>
                      <select
                        value={partnerFilter}
                        onChange={(e) => setPartnerFilter(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      >
                        <option value="all">All Rentals</option>
                        <option value="with-partner">With Partner</option>
                        <option value="without-partner">Without Partner</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <DatePicker
                        selected={dateFilter.startDate}
                        onChange={(date) =>
                          setDateFilter((prev) => ({
                            ...prev,
                            startDate: date,
                          }))
                        }
                        placeholderText="Start Date"
                        wrapperClassName="w-full"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                        dateFormat="dd/MM/yyyy"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <DatePicker
                        selected={dateFilter.endDate}
                        onChange={(date) =>
                          setDateFilter((prev) => ({ ...prev, endDate: date }))
                        }
                        placeholderText="End Date"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                        dateFormat="dd/MM/yyyy"                        minDate={dateFilter.startDate}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

          {/* Rentals Section - Standard View */}
          <div className="grid grid-cols-1 gap-6">
            {rentalsLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="flex flex-col items-center space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  <p className="text-gray-500">Loading rentals...</p>
                </div>
              </div>
            ) : filteredRentals && filteredRentals.length > 0 ? (              filteredRentals.map((rental) => (
                <motion.div
                  key={rental._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200"
                >
                  <div className="p-4">
                    {/* Mobile Header - Always Visible */}
                    <div 
                      className={`flex ${isMobile ? 'justify-between items-center cursor-pointer' : 'flex-col md:flex-row justify-between items-start md:items-center'} gap-4`}
                      onClick={() => isMobile && toggleRentalExpansion(rental._id)}
                    >
                      {/* Customer Info - Compact for Mobile */}
                      <div className={`flex items-start ${isMobile ? 'flex-col' : 'space-x-4'}`}>
                        <div className={`bg-blue-100 rounded-full p-2 ${isMobile ? 'mb-2' : ''}`}>
                          <FaUser className={`${isMobile ? 'w-4 h-4' : 'w-6 h-6'} text-blue-600`} />
                        </div>
                        <div>
                          <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>
                            {rental.customer.firstName} {rental.customer.lastName}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {rental.carName}
                          </p>
                          {!isMobile && (
                            <>
                              <p className="text-sm text-gray-500">
                                {rental.customer.email}
                              </p>
                              <p className="text-sm text-gray-500">
                                Phone: {rental.customer.phoneNumber} | Age: {rental.customer.age}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Status and Quick Actions */}
                      <div className={`flex ${isMobile ? 'items-center space-x-2' : 'flex-col md:flex-row gap-2 w-full md:w-auto'}`}>
                        {!isMobile && (
                          <select
                            value={rental.status}
                            onChange={(e) => {
                              handleRentalStatusUpdate(rental._id, e.target.value);
                              setTimeout(() => refetch(), 3000);
                            }}
                            disabled={updatingRentalId === rental._id}
                            className={`px-4 py-2 rounded-full border ${
                              rental.status === "pending"
                                ? "bg-yellow-100 text-yellow-800 border-yellow-300"                                : rental.status === "confirmed"
                                ? "bg-green-100 text-green-800 border-green-300"
                                : rental.status === "completed"
                                ? "bg-blue-100 text-blue-800 border-blue-300"
                                : "bg-red-100 text-red-800 border-red-300"
                            }`}
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        )}
                        
                        {/* Status Badge for Mobile */}
                        {isMobile && (
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            rental.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : rental.status === "confirmed"
                              ? "bg-green-100 text-green-800"
                              : rental.status === "completed"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-red-100 text-red-800"
                          }`}>
                            {rental.status}
                          </span>
                        )}
                        
                        {/* Expand/Collapse Icon for Mobile */}
                        {isMobile && (
                          <svg 
                            className={`w-5 h-5 transition-transform ${expandedRentals.has(rental._id) ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Collapsible Details Section */}
                    {(!isMobile || expandedRentals.has(rental._id)) && (
                      <div className="mt-4 border-t pt-4">
                        {/* Full Details for Desktop or Expanded Mobile */}
                        {isMobile && (
                          <div className="mb-4 space-y-2">
                            <p className="text-sm text-gray-500">{rental.customer.email}</p>
                            <p className="text-sm text-gray-500">
                              Phone: {rental.customer.phoneNumber} | Age: {rental.customer.age}
                            </p>
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'gap-2'} mb-4`}>
                          {isMobile && (
                            <select
                              value={rental.status}
                              onChange={(e) => {
                                handleRentalStatusUpdate(rental._id, e.target.value);
                                setTimeout(() => refetch(), 3000);
                              }}
                              disabled={updatingRentalId === rental._id}
                              className={`px-4 py-2 rounded-lg border w-full ${
                                rental.status === "pending"
                                  ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                                  : rental.status === "confirmed"
                                  ? "bg-green-100 text-green-800 border-green-300"
                                  : rental.status === "completed"
                                  ? "bg-blue-100 text-blue-800 border-blue-300"
                                  : "bg-red-100 text-red-800 border-red-300"
                              }`}
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          )}
                          
                          <div className={`flex ${isMobile ? 'justify-around' : 'gap-2'}`}>
                            <button
                              onClick={() => handleOpenRentalDetails(rental)}
                              className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex-1 md:flex-none"
                              title="View Details"
                            >
                              <FaEye className="w-4 h-4 mx-auto" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedRental(rental);
                                setIsEditRentalModalOpen(true);
                              }}
                              disabled={updatingRentalId === rental._id}
                              className="p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex-1 md:flex-none"
                            >
                              <FaEdit className="w-4 h-4 mx-auto" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedRentalForPartner(rental);
                                setIsAssignPartnerModalOpen(true);
                              }}
                              className="p-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex-1 md:flex-none"
                              title="Assign to Partner"
                            >
                              <FaHandshake className="w-4 h-4 mx-auto" />
                            </button>
                            <button
                              onClick={() => {
                                handleRentalDelete(rental._id);
                                setTimeout(() => refetch(), 3000);
                              }}
                              disabled={deletingRentalId === rental._id}
                              className="p-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex-1 md:flex-none"
                            >
                              {deletingRentalId === rental._id ? (
                                <FaSync className="w-4 h-4 animate-spin mx-auto" />
                              ) : (
                                <FaTrash className="w-4 h-4 mx-auto" />
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Rental Details Grid */}
                        <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'}`}>
                      {/* Vehicle Details */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">
                          Vehicle Details
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4 h-full">
                          <p className="text-base font-medium text-gray-900">
                            {rental.carName}
                          </p>
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <p>Category: {rental.car?.category}</p>
                            <p>Transmission: {rental.car?.transmission}</p>
                            <p>
                              Seats: {rental.car?.seats || rental.car?.persons}
                            </p>
                            {rental.licensePlate ? (
                              <p className="text-green-600 font-medium">
                                Plate: {rental.licensePlate}
                              </p>
                            ) : (
                              <p className="text-orange-500">
                                No vehicle assigned yet
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Partner Information */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">
                          Partner Assignment
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col justify-center">
                          {rental.professionalPartner ? (
                            <div className="space-y-2">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                                  <FaHandshake className="h-4 w-4 text-green-600" />
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-gray-900">
                                    {rental.professionalPartner.firstName} {rental.professionalPartner.lastName}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {rental.professionalPartner.email}
                                  </p>
                                </div>
                              </div>
                              <div className="mt-2 text-sm text-gray-600">
                                <p className="text-green-600 font-medium">
                                  Commission: €{rental.commissionAmount?.toFixed(2) || '0.00'}
                                </p>
                                <p className={`text-xs ${rental.commissionPaid ? 'text-green-600' : 'text-orange-600'}`}>
                                  Payment Status: {rental.commissionPaid ? 'Paid' : 'Pending'}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-400">
                              <div className="text-center">
                                <FaUser className="h-8 w-8 mx-auto mb-2" />
                                <p className="text-sm">No partner assigned</p>
                                <button
                                  onClick={() => {
                                    setSelectedRentalForPartner(rental);
                                    setIsAssignPartnerModalOpen(true);
                                  }}
                                  className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                                >
                                  Assign Partner
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Rental Period */}
                                  <div className="space-y-2">
                                  <h4 className="text-sm font-medium text-gray-700">
                                    Rental Period
                                  </h4>
                                  <div className="bg-gray-50 rounded-lg p-4 space-y-3 h-full flex flex-col justify-center">
                                    <div className="flex items-center text-sm">
                                    <FaCalendarAlt className="w-4 h-4 text-green-500 mr-2" />
                                    <div>
                                      <p className="font-medium">
                                      Pickup: {formatDateTime(rental.pickupDateTime)}
                                      </p>
                                      <p className="text-gray-500 mt-1">
                                      Location: {rental.pickupLocation}
                                      </p>
                                    </div>
                                    </div>
                                    <div className="flex items-center text-sm">
                                    <FaCalendarAlt className="w-4 h-4 text-red-500 mr-2" />
                                    <div>
                                      <p className="font-medium">
                                      Return: {formatDateTime(rental.returnDateTime)}
                                      </p>
                                      <p className="text-gray-500 mt-1">
                                      Location: {rental.returnLocation}
                                      </p>
                                    </div>
                                    </div>
                                  </div>
                      </div>
                      {/* Additional Options & Notes */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">
                          Additional Options & Notes
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col">                          {/* Pre-Invoice PDF Button */}
                          {rental.preInvoicePdfUrl && (
                            <button
              onClick={async () => {
                try {
                  const response = await RentalsApi.getPreInvoicePdf(rental._id);
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const pdfUrl = window.URL.createObjectURL(blob);
                  window.open(pdfUrl, '_blank');
                  // Clean up the object URL after a short delay
                  setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 1000);
                } catch (error) {
                  console.error('Error opening PDF:', error);
                  alert('Failed to open PDF. Please try again.');
                }
              }}
                              className="mb-3 px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-md hover:bg-indigo-700 flex items-center justify-center"
                            >
                              <FaFileInvoice className="w-3 h-3 mr-1" />
                              View Pre-Invoice PDF
                            </button>
                          )}
                          {/* Existing Options */}
                          {rental.additionalOptions &&
                          rental.additionalOptions.length > 0 ? (
                            (() => {
                              const rentalDays = calculateRentalDays(
                                rental.pickupDateTime,
                                rental.returnDateTime
                              );

                              return (
                                <div className="space-y-2 mb-3">
                                  <p className="text-sm font-medium text-gray-700">
                                    Additional Options ({rentalDays} day
                                    {rentalDays > 1 ? "s" : ""}):
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {rental.additionalOptions.map(
                                      (option, index) => (
                                        <span
                                          key={index}
                                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                        >
                                          {option.name} - €
                                          {(option.price || 0).toFixed(2)}/day
                                          (Total: €
                                          {(
                                            (option.price || 0) * rentalDays
                                          ).toFixed(2)}
                                          )
                                        </span>
                                      )
                                    )}
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            <div className="mb-3">
                              <span className="text-sm text-gray-500">
                                No additional options selected
                              </span>
                            </div>
                          )}
                          {rental.additionalNotes && (
                            <div className="mt-3">
                              <p className="text-sm font-medium text-gray-700">
                                Notes:
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                {rental.additionalNotes}
                              </p>
                            </div>
                          )}
                          {rental.status === "cancelled" && rental.cancellationReason && (
                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <p className="text-sm font-medium text-red-700 flex items-center">
                                <FaExclamationTriangle className="w-4 h-4 mr-2" />
                                Cancellation Reason:
                              </p>
                              <p className="text-sm text-red-600 mt-1 italic">
                                {rental.cancellationReason}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Price Breakdown */}
                    <div className="mt-6">
                      <button
                        onClick={() =>
                          setExpandedCustomer(
                            expandedCustomer === rental._id ? null : rental._id
                          )
                        }
                        className="flex items-center text-sm font-medium pt-4 text-gray-500 hover:text-gray-700"
                      >
                        {expandedCustomer === rental._id ? (
                          <>
                            <FaChevronUp className="mr-1" />
                            Hide Price Details
                          </>
                        ) : (
                          <>
                            <FaChevronDown className="mr-1" />
                            Show Price Details
                          </>
                        )}
                      </button>

                      <AnimatePresence>
                        {expandedCustomer === rental._id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-4 bg-gray-50 rounded-lg p-4">
                              <h4 className="text-sm font-medium text-gray-700 mb-4">
                                Price Breakdown
                              </h4>
                              <div className="space-y-2">
                                {rental.priceBreakdown.map((period, index) => (
                                  <div
                                    key={index}
                                    className="flex justify-between text-sm"
                                  >
                                    <span>
                                      {new Date(
                                        period.startDate
                                      ).toLocaleDateString()}{" "}
                                      -{" "}
                                      {new Date(
                                        period.endDate
                                      ).toLocaleDateString()}{" "}
                                    </span>
                                    <span>
                                      €{period.pricePerDay} ×{" "}
                                      {period.numberOfDays} days = €
                                      {period.totalForPeriod}
                                    </span>
                                  </div>
                                ))}

                                {rental.additionalOptions &&
                                  rental.additionalOptions.length > 0 &&
                                  (() => {
                                    const rentalDays = calculateRentalDays(
                                      rental.pickupDateTime,
                                      rental.returnDateTime
                                    );
                                    const totalAdditionalOptionsPrice =
                                      calculateAdditionalOptionsPrice(
                                        rental.additionalOptions,
                                        rentalDays
                                      );

                                    return (
                                      <div className="space-y-1">
                                        <div className="flex justify-between text-sm font-medium text-gray-700 pt-2 border-t border-gray-200">
                                          <span>
                                            Additional Options ({rentalDays} day
                                            {rentalDays > 1 ? "s" : ""}):
                                          </span>
                                          <span>
                                            €
                                            {totalAdditionalOptionsPrice.toFixed(
                                              2
                                            )}
                                          </span>
                                        </div>
                                        {rental.additionalOptions.map(
                                          (option, index) => (
                                            <div
                                              key={index}
                                              className="flex justify-between text-xs text-gray-600 ml-4"
                                            >
                                              <span>• {option.name}</span>
                                              <span>
                                                €{option.price.toFixed(2)}/day ×{" "}
                                                {rentalDays} = €
                                                {(
                                                  option.price * rentalDays
                                                ).toFixed(2)}
                                              </span>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    );
                                  })()}

                                {rental.discountAmount > 0 && (
                                  <div className="flex justify-between text-sm text-green-600">
                                    <span>
                                      Discount{" "}
                                      {rental.couponCode
                                        ? `(${rental.couponCode})`
                                        : ""}
                                    </span>
                                    <span>-€{rental.discountAmount}</span>
                                  </div>
                                )}

                                <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                                  <span>Before VAT:</span>
                                  <span>
                                    €{(rental.totalPrice / 1.24).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span>VAT (24%):</span>
                                  <span>
                                    €
                                    {(
                                      rental.totalPrice -
                                      rental.totalPrice / 1.24
                                    ).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-base font-medium pt-2 border-t border-gray-200">
                                  <span>Total Price (inc. VAT):</span>
                                  <span>
                                    €
                                    {rental.additionalOptionsPrice
                                      ? rental.totalPrice -
                                        rental.additionalOptionsPrice +
                                        calculateAdditionalOptionsPrice(
                                          rental.additionalOptions,
                                          calculateRentalDays(
                                            rental.pickupDateTime,
                                            rental.returnDateTime
                                          )
                                        )
                                      : rental.totalPrice}
                                  </span>
                                </div>

                                {rental.advancePayment > 0 && (
                                  <>
                                    <div className="flex justify-between text-sm text-amber-600 pt-2 border-t border-gray-200">
                                      <span>Advance Payment:</span>
                                      <span>-€{rental.advancePayment}</span>
                                    </div>
                                    <div className="flex justify-between text-base font-medium pt-2 mt-1 bg-blue-50 p-2 rounded-md border border-blue-100">
                                      <span>Balance Due:</span>
                                      <span>
                                        €
                                        {(
                                          rental.totalPrice -
                                          rental.advancePayment
                                        ).toFixed(2)}
                                      </span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>                    {/* Handover Information */}
                    {renderHandoverInfo(rental)}

                    {/* Submission Buttons */}
                    {renderSubmissionButtons(rental)}
                        </div>
                      )}
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-500">
                  <FaList className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-lg font-medium">No rentals found</p>
                  <p className="text-sm">Try adjusting your search or filters</p>
                </div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalRentalPages > 1 && (
            <div className="flex justify-center mt-6">
              <nav className="inline-flex rounded-md shadow-sm -space-x-px">
                {/* Previous Page */}
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || rentalsLoading}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rentalsLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                  ) : (
                    <FaChevronLeft className="h-5 w-5" />
                  )}
                </button>

                {/* Page Indicators */}
                {Array.from(
                  { length: Math.min(5, totalRentalPages) },
                  (_, i) => {
                    // Show pages around the current page
                    let pageNum;
                    if (totalRentalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalRentalPages - 2) {
                      pageNum = totalRentalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={rentalsLoading}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        } ${rentalsLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}

                {/* Next Page */}
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalRentalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalRentalPages || rentalsLoading}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rentalsLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                  ) : (
                    <FaChevronRight className="h-5 w-5" />
                  )}
                </button>
              </nav>
            </div>          )}
        </div>
      )}

      {/* Compact View Tab Content */}
      {activeTab === "compact-view" && (
        <div className="space-y-4">
          {/* Search and Filter Section - Collapsible */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div 
              className="flex items-center justify-between p-4 cursor-pointer"
              onClick={() => setIsCompactViewExpanded(!isCompactViewExpanded)}
            >
              <h3 className="text-lg font-semibold text-gray-800">Compact View - Search & Filters</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {filteredRentals?.length || 0} results
                </span>
                <svg 
                  className={`w-5 h-5 transition-transform ${isCompactViewExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            
            {isCompactViewExpanded && (
              <div className="border-t p-4">
                {/* Basic Search and Action Buttons */}
                <div className={`flex ${isMobile ? 'flex-col' : 'flex-col md:flex-row'} gap-4 items-center w-full justify-between`}>
                  <div className="relative w-full md:w-1/2">
                    <input
                      type="text"
                      placeholder="Search by name, car, location..."
                      value={rentalSearch}
                      onChange={(e) => setRentalSearch(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10"
                    />
                    <FaSearch className="absolute left-3 top-3 text-gray-400" />
                  </div>

                  <div className={`flex ${isMobile ? 'flex-col w-full' : 'md:flex-row flex-col'} gap-2 w-full md:w-auto`}>
                    <motion.button
                      onClick={() => setIsRentalModalOpen(true)}
                      className={`px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-300 flex items-center justify-center ${isMobile ? 'mb-2' : ''}`}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <FaPlus className="w-4 h-4 mr-2" />
                      <span>Create Rental</span>
                    </motion.button>
                    <motion.button
                      onClick={() => setIsCombinedModalOpen(true)}
                      className={`px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-colors duration-300 flex items-center justify-center ${isMobile ? 'mb-2' : ''}`}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <FaPlus className="w-4 h-4 mr-2" />
                      <span>Create Combined</span>
                    </motion.button>
                    <button
                      onClick={fetchData}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                    >
                      <FaSync className="w-4 h-4" />
                      {!isMobile && <span className="ml-2">Refresh</span>}
                    </button>
                  </div>
                </div>              </div>
            )}
          </div>

          {/* Compact Table View */}
                              {/* Compact Table View */}
          <div className="bg-white overflow-hidden rounded-lg shadow border border-gray-200">
            <div className={`${isMobile ? 'overflow-x-auto' : 'overflow-x-auto'}`}>
              <table className="min-w-full divide-y divide-gray-200 table-auto">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isMobile ? 'w-1/4' : 'w-1/6'}`}
                    >
                      Customer
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6"
                    >
                      Vehicle
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5"
                    >
                      Dates & Locations
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8"
                    >
                      Status
                    </th>                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8"
                    >
                      Payment
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8"
                    >
                      Partner
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6"
                    >
                      Handovers & Submissions
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>                <tbody className="bg-white divide-y divide-gray-200">
                  {rentalsLoading ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center space-y-4">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                          <p className="text-gray-500">Loading rentals...</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredRentals && filteredRentals.length > 0 ? (
                    filteredRentals.map((rental) => (
                      <tr
                        key={rental._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        {/* Customer */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <FaUser className="h-4 w-4 text-blue-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {rental.customer.firstName}{" "}
                                {rental.customer.lastName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {rental.customer.email}
                              </div>
                              <div className="text-xs text-gray-500">
                                {rental.customer.phoneNumber}
                              </div>
                            </div>
                          </div>
                        </td>{" "}
                        {/* Vehicle */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {rental.carName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {rental.car?.category || 'Unknown'} |{" "}
                            {rental.car?.transmission?.toUpperCase() || 'Unknown'}
                          </div>
                          {rental.additionalOptions &&
                            rental.additionalOptions.length > 0 &&
                            (() => {
                              const rentalDays = calculateRentalDays(
                                rental.pickupDateTime,
                                rental.returnDateTime
                              );
                              const totalAdditionalOptionsPrice =
                                calculateAdditionalOptionsPrice(
                                  rental.additionalOptions,
                                  rentalDays
                                );

                              return (
                                <div className="mt-1">
                                  <div className="text-xs text-blue-600 font-medium">
                                    {rental.additionalOptions.length} option
                                    {rental.additionalOptions.length > 1
                                      ? "s"
                                      : ""}{" "}
                                    (+€{totalAdditionalOptionsPrice.toFixed(2)})
                                  </div>
                                  <div
                                    className="text-xs text-gray-500 truncate max-w-32"
                                    title={rental.additionalOptions
                                      .map(
                                        (opt) =>
                                          `${opt.name} (€${(opt.price || 0).toFixed(
                                            2
                                          )}/day)`
                                      )
                                      .join(", ")}
                                  >
                                    {rental.additionalOptions
                                      .map((opt) => opt.name)
                                      .join(", ")}
                                  </div>
                                </div>
                              );
                            })()}
                        </td>{" "}
                        {/* Dates & Locations */}
                        <td className="px-4 py-4 max-w-xs">
                          <div className="space-y-2">
                            {/* Pickup Section */}
                            <div className="bg-green-50 rounded-md p-2 border-l-2 border-green-400">
                              <div className="flex items-center text-xs font-medium text-green-800 mb-1">
                                <FaCalendarAlt className="mr-1 h-3 w-3" />
                                <span>Pickup</span>
                              </div>
                              <div className="text-xs text-green-700 font-medium">
                                {moment(rental.pickupDateTime).format(
                                  "DD/MM/YY HH:mm"
                                )}
                              </div>
                              <div
                                className="text-xs text-green-600 truncate"
                                title={rental.pickupLocation}
                              >
                                <TbGps className="inline mr-1 h-3 w-3" />
                                {rental.pickupLocation.length > 25
                                  ? `${rental.pickupLocation.substring(
                                      0,
                                      25
                                    )}...`
                                  : rental.pickupLocation}
                              </div>
                            </div>

                            {/* Return Section */}
                            <div className="bg-red-50 rounded-md p-2 border-l-2 border-red-400">
                              <div className="flex items-center text-xs font-medium text-red-800 mb-1">
                                <FaCalendarAlt className="mr-1 h-3 w-3" />
                                <span>Return</span>
                              </div>
                              <div className="text-xs text-red-700 font-medium">
                                {moment(rental.returnDateTime).format(
                                  "DD/MM/YY HH:mm"
                                )}
                              </div>
                              <div
                                className="text-xs text-red-600 truncate"
                                title={rental.returnLocation}
                              >
                                <TbGps className="inline mr-1 h-3 w-3" />
                                {rental.returnLocation.length > 25
                                  ? `${rental.returnLocation.substring(
                                      0,
                                      25
                                    )}...`
                                  : rental.returnLocation}
                              </div>
                            </div>

                            {/* Duration Badge */}
                            <div className="text-center">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                {calculateRentalDays(
                                  rental.pickupDateTime,
                                  rental.returnDateTime
                                )}{" "}
                                day
                                {calculateRentalDays(
                                  rental.pickupDateTime,
                                  rental.returnDateTime
                                ) > 1
                                  ? "s"
                                  : ""}
                              </span>
                            </div>
                          </div>
                        </td>
                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select                            value={rental.status}
                            onChange={(e) => {
                              handleRentalStatusUpdate(
                                rental._id,
                                e.target.value
                              );
                              setTimeout(() => refetch(), 3000);
                            }}
                            disabled={updatingRentalId === rental._id}
                            className={`text-xs px-2.5 py-1.5 rounded-full border ${
                              rental.status === "pending"
                                ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                                : rental.status === "confirmed"
                                ? "bg-green-100 text-green-800 border-green-300"
                                : rental.status === "completed"
                                ? "bg-blue-100 text-blue-800 border-blue-300"
                                : "bg-red-100 text-red-800 border-red-300"
                            }`}
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          {rental.status === "cancelled" && rental.cancellationReason && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                              <p className="text-xs font-medium text-red-700 flex items-center">
                                <FaExclamationTriangle className="w-3 h-3 mr-1" />
                                Reason:
                              </p>
                              <p className="text-xs text-red-600 mt-1 italic break-words">
                                {rental.cancellationReason.length > 50 
                                  ? `${rental.cancellationReason.substring(0, 50)}...` 
                                  : rental.cancellationReason}
                              </p>
                            </div>
                          )}
                          {rental.aade && (
                            <div className="mt-2 flex items-center">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${
                                  rental.aade.submitted
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                                title={
                                  rental.aade.submitted
                                    ? `AADE Submitted: ${moment(
                                        rental.aade.submissionDate
                                      ).format("DD/MM/YYYY")}`
                                    : "Not submitted to AADE"
                                }
                              >
                              {rental.aade.submitted ? (
                                <>
                                <span className="mr-1">
                                  AADE ✓
                                </span>
                                </>
                                
                              ) : (
                                <>
                                <span className="mr-1">
                                  AADE ✗
                                </span>
                                </>
                              )}
                              </span>
                            </div>
                          )}
                                                         <span className="mt-2 text-gray-600 text-xs">Plate: {rental.licensePlate}</span>

                        </td>
                        {/* Payment */}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">
                                      {(() => {
                                        const finalPrice = rental.additionalOptionsPrice
                                        ? rental.totalPrice -
                                          rental.additionalOptionsPrice +
                                          calculateAdditionalOptionsPrice(
                                          rental.additionalOptions,
                                          calculateRentalDays(
                                            rental.pickupDateTime,
                                            rental.returnDateTime
                                          )
                                          )
                                        : rental.totalPrice;
                                        
                                        return finalPrice === 0 
                                        ? "N/A"
                                        : `€${finalPrice.toFixed(2)}`;
                                      })()}
                                      </div>
                                      {rental.additionalOptions &&
                                      rental.additionalOptions.length > 0 &&
                                      (() => {
                              const rentalDays = calculateRentalDays(
                                rental.pickupDateTime,
                                rental.returnDateTime
                              );
                              const totalAdditionalOptionsPrice =
                                calculateAdditionalOptionsPrice(
                                  rental.additionalOptions,
                                  rentalDays
                                );
                              const optionsDetails = rental.additionalOptions
                                .map(
                                  (opt) =>
                                    `${opt.name} (€${(opt.price || 0).toFixed(
                                      2
                                    )}/day × ${rentalDays} = €${(
                                      (opt.price || 0) * rentalDays
                                    ).toFixed(2)})`
                                )
                                .join(", ");

                              return (
                                <div
                                  className="mt-1 text-xs text-gray-500"
                                  title={`Additional Options (${rentalDays} day${
                                    rentalDays > 1 ? "s" : ""
                                  }): ${optionsDetails}`}
                                >
                                  €{totalAdditionalOptionsPrice.toFixed(2)}{" "}
                                  extras ({rentalDays}d)
                                </div>
                              );
                            })()}
                          <select                            value={rental.paymentStatus}
                            onChange={(e) => {
                              handlePaymentStatusUpdate(
                                rental._id,
                                e.target.value
                              );
                              setTimeout(() => refetch(), 3000);
                            }}
                            disabled={updatingPaymentId === rental._id}
                            className={`mt-1 text-xs px-2.5 py-1 rounded-full border ${
                              rental.paymentStatus === "pending"
                                ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                                : rental.paymentStatus === "partial"
                                ? "bg-orange-100 text-orange-800 border-orange-300"
                                : rental.paymentStatus === "paid"
                                ? "bg-green-100 text-green-800 border-green-300"
                                : "bg-red-100 text-red-800 border-red-300"
                            }`}
                          >
                            <option value="pending">Pending</option>
                            <option value="paid">Paid</option>
                          </select>
                          {rental.advancePayment > 0 && (
                            <div className="mt-1 text-xs text-blue-600">
                              Advance: €{rental.advancePayment}
                            </div>
                          )}                          {/* Pre-Invoice PDF Button */}
                          {rental.preInvoicePdfUrl && (
                            <button
              onClick={async () => {
                try {
                  const response = await RentalsApi.getPreInvoicePdf(rental._id);
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const pdfUrl = window.URL.createObjectURL(blob);
                  window.open(pdfUrl, '_blank');
                  // Clean up the object URL after a short delay
                  setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 1000);
                } catch (error) {
                  console.error('Error opening PDF:', error);
                  alert('Failed to open PDF. Please try again.');
                }
              }}
                              className="mt-2 px-2.5 py-1 bg-indigo-600 text-white text-xs rounded-md hover:bg-indigo-700 flex items-center text-center w-full"
                            >
                              <FaFileInvoice className="w-3 h-3 mr-1" />
                              View Pre-Invoice
                            </button>
                          )}
                        </td>
                        {/* Partner */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {rental.professionalPartner ? (
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-6 w-6 bg-green-100 rounded-full flex items-center justify-center">
                                <FaHandshake className="h-3 w-3 text-green-600" />
                              </div>
                              <div className="ml-2">
                                <div className="text-xs font-medium text-gray-900">
                                  {rental.professionalPartner.firstName} {rental.professionalPartner.lastName}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Commission: €{rental.commissionAmount?.toFixed(2) || '0.00'}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center text-gray-400">
                              <FaUser className="h-3 w-3 mr-1" />
                              <span className="text-xs">No partner</span>
                            </div>
                          )}
                        </td>
                        {/* Handovers & Submissions */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            {/* Handovers Summary */}
                            <div className="text-xs">
                              {loadingHandovers[rental._id] ? (
                                <div className="flex items-center text-gray-500">
                                  <FaSync className="w-3 h-3 mr-1 animate-spin" />
                                  Loading...
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  {(handoversData[rental._id] || []).map(
                                    (handover, idx) => (
                                      <div
                                        key={idx}
                                        className="flex items-center space-x-1"
                                      >
                                        <span
                                          className={`px-1.5 py-0.5 rounded text-xs ${
                                            handover.handoverType === "pickup"
                                              ? "bg-green-100 text-green-700"
                                              : "bg-blue-100 text-blue-700"
                                          }`}
                                        >
                                          {handover.handoverType === "pickup"
                                            ? "P"
                                            : "R"}
                                        </span>
                                        <span className="text-xs text-gray-600">
                                          {moment(handover.createdAt).format(
                                            "DD/MM"
                                          )}
                                        </span>
                                      </div>
                                    )
                                  )}
                                  {(!handoversData[rental._id] ||
                                    handoversData[rental._id].length === 0) && (
                                    <span className="text-gray-500">
                                      No handovers
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Submission Buttons */}
                            {hasCompletedPickupHandover(rental._id) && (
                              <div className="flex space-x-1">
                                {!rental.aade?.submitted ? (
                                  <button
                                    onClick={() => handleAADESubmission(rental)}
                                    disabled={
                                      submittingAADE === rental._id ||
                                      !rental.licensePlate
                                    }
                                    className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                                    title="Submit to AADE"
                                  >
                                    {submittingAADE === rental._id ? (
                                      <FaSync className="w-3 h-3 animate-spin" />
                                    ) : (
                                      "AADE"
                                    )}
                                  </button>
                                ) : (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                    AADE ✓
                                  </span>
                                )}

                                {!rental.oxygenInvoiceId ? (
                                  <button
                                    onClick={() =>
                                      handleOxygenSubmission(rental)
                                    }
                                    // disabled={submittingOxygen === rental._id || !rental.invoiceData?.vatNumber}
                                    disabled={submittingOxygen === rental._id}
                                    className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 disabled:opacity-50"
                                    title="Create Oxygen invoice"
                                  >
                                    {submittingOxygen === rental._id ? (
                                      <FaSync className="w-3 h-3 animate-spin" />
                                    ) : (
                                      "Invoice"
                                    )}
                                  </button>
                                ) : (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                    Invoice ✓
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleOpenRentalDetails(rental)}
                              className="p-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                              title="View Details"
                            >
                              <FaEye className="w-3.5 h-3.5" />
                            </button>                            <button
                              onClick={() => {
                                setSelectedRental(rental);
                                setIsEditRentalModalOpen(true);
                              }}
                              disabled={updatingRentalId === rental._id}
                              className="p-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <FaEdit className="w-3.5 h-3.5" />
                            </button>
                            
                            
                            <button
                              onClick={() => {
                                setSelectedRentalForPartner(rental);
                                setIsAssignPartnerModalOpen(true);
                              }}
                              className="p-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                              title="Assign to Partner"
                            >
                              <FaHandshake className="w-3.5 h-3.5" />
                            </button>                            <button
                              onClick={() => {
                                handleRentalDelete(rental._id);
                                setTimeout(() => refetch(), 3000);
                              }}
                              disabled={deletingRentalId === rental._id}
                              className="p-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {deletingRentalId === rental._id ? (
                                <FaSync className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <FaTrash className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center">
                        <div className="text-gray-500">
                          <FaList className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <p className="text-lg font-medium">No rentals found</p>
                          <p className="text-sm">Try adjusting your search or filters</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* Pagination */}
          {totalRentalPages > 1 && (
            <div className="flex justify-center mt-6">
              <nav className="inline-flex rounded-md shadow-sm -space-x-px">
                {/* Previous Page */}                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || rentalsLoading}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rentalsLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                  ) : (
                    <FaChevronLeft className="h-5 w-5" />
                  )}
                </button>

                {/* Page Indicators */}
                {Array.from(
                  { length: Math.min(5, totalRentalPages) },
                  (_, i) => {
                    // Show pages around the current page
                    let pageNum;
                    if (totalRentalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalRentalPages - 2) {
                      pageNum = totalRentalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (                      <button
                        key={i}
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={rentalsLoading}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        } ${rentalsLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}

                {/* Next Page */}                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalRentalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalRentalPages || rentalsLoading}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rentalsLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                  ) : (
                    <FaChevronRight className="h-5 w-5" />
                  )}                </button>
              </nav>
            </div>
          )}
        </div>
      )}
      
      {/* Quick Rental Creator Tab Content */}
      {activeTab === "quick-rental-creator" && (
                            <motion.div
                                key="quick-rental-creator"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="mb-6">
                                    <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-indigo-500">
                                        <h2 className="text-xl font-bold text-gray-800 mb-4">
                                            Quick Rental Creator
                                        </h2>
                                        
                                        <p className="text-gray-600 mb-6">
                                            Create rentals quickly and submit them directly to AADE. Fill in the license plate and dates, then click 'Create Rentals'.
                                        </p>
                                        
                                        {/* Common pickup/return locations */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Default Pickup Location
                                                </label>
                                                <input
                                                    type="text"
                                                    value={pickupLocation}
                                                    onChange={(e) => setPickupLocation(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Default Return Location
                                                </label>
                                                <input
                                                    type="text"
                                                    value={returnLocation}
                                                    onChange={(e) => setReturnLocation(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">License Plate</th>
                                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Car</th>
                                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pickup</th>
                                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Return</th>
                                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submit to AADE</th>
                                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {quickRentalRows.map((row, index) => (
                                                        <motion.tr 
                                                            key={row.id}
                                                            initial={{ opacity: 0, y: 20 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ duration: 0.3, delay: index * 0.1 }}
                                                        >
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <select
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                                    value={row.licensePlate || ''}
                                                                    onChange={(e) => {
                                                                        const selectedPlateInfo = availableLicensePlates.find(
                                                                            plate => plate.plate === e.target.value
                                                                        );
                                                                        handleLicensePlateChange(index, selectedPlateInfo);
                                                                    }}
                                                                >
                                                                    <option value="">Select license plate</option>
                                                                    {availableLicensePlates.map(plate => (
                                                                        <option key={plate.plate} value={plate.plate}>
                                                                            {plate.plateWithCar}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {row.carName || '-'}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {row.customer.firstName} - {row.customer.lastName}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <DatePicker
                                                                    selected={row.pickupDateTime}
                                                                    onChange={(date) => handleDateChange(index, 'pickupDateTime', date)}
                                                                    showTimeSelect
                                                                    dateFormat="dd/MM/yyyy HH:mm"
                                                                    className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                                />
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <DatePicker
                                                                    selected={row.returnDateTime}
                                                                    onChange={(date) => handleDateChange(index, 'returnDateTime', date)}
                                                                    showTimeSelect
                                                                    dateFormat="dd/MM/yyyy HH:mm"
                                                                    className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                                />
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                                                                    <input 
                                                                        type="checkbox" 
                                                                        className="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                                                                        checked={row.submitToAade}
                                                                        onChange={() => toggleSubmitToAade(index)}
                                                                        style={{
                                                                            transition: 'all 0.3s',
                                                                            left: row.submitToAade ? '50%' : '0%',
                                                                            borderColor: row.submitToAade ? '#4f46e5' : '#cbd5e1'
                                                                        }}
                                                                    />
                                                                    <label 
                                                                        className="block h-6 overflow-hidden rounded-full cursor-pointer"
                                                                        style={{
                                                                            background: row.submitToAade ? '#4f46e5' : '#cbd5e1',
                                                                            transition: 'all 0.3s'
                                                                        }}
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <motion.button
                                                                    whileHover={{ scale: 1.2 }}
                                                                    whileTap={{ scale: 0.9 }}
                                                                    onClick={() => deleteQuickRentalRow(index)}
                                                                    className="text-red-600 hover:text-red-900"
                                                                    title="Delete Row"
                                                                >
                                                                    <TrashIcon className="h-5 w-5" />
                                                                </motion.button>
                                                            </td>
                                                        </motion.tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    
                                    <div className="flex justify-end space-x-4">
                                        <motion.button
                                            whileHover={{ scale: 1.05, boxShadow: "0 4px 6px rgba(79, 70, 229, 0.1)" }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={addQuickRentalRow}
                                            className="px-4 py-2 border border-indigo-500 text-indigo-600 rounded-lg flex items-center space-x-2 hover:bg-indigo-50 transition-all duration-200"
                                        >
                                            <PlusCircleIcon className="w-5 h-5" />
                                            <span>Add Row</span>
                                        </motion.button>
                                        
                                        <motion.button
                                            whileHover={{ scale: 1.05, boxShadow: "0 4px 6px rgba(16, 185, 129, 0.1)" }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={createQuickRentals}
                                            disabled={creatingRental || !quickRentalRows.some(row => row.licensePlate && row.car)}
                                            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200
                                                ${creatingRental || !quickRentalRows.some(row => row.licensePlate && row.car)
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                                                }`}
                                        >
                                            <ArrowDownTrayIcon className="w-5 h-5" />
                                            <span>{creatingRental ? 'Creating...' : 'Create Rentals'}</span>
                                        </motion.button>
                                    </div>
                                </div>
                            </motion.div>
      )}
      {/* Rental Details Modal */}
      <RentalDetailsModal
        isOpen={isRentalDetailsModalOpen}
        onClose={() => setIsRentalDetailsModalOpen(false)}
        rental={selectedModalRental}      />

      <CombinedRentalHandoverModal 
        isOpen={isCombinedModalOpen}
        onClose={() => setIsCombinedModalOpen(false)}        onSuccess={() => {
          setIsCombinedModalOpen(false);
          refetch();
        }}
      /> 

      {/* Assign Partner Modal */}
      <AssignPartnerModal
        isOpen={isAssignPartnerModalOpen}
        onClose={() => {
          setIsAssignPartnerModalOpen(false);
          setSelectedRentalForPartner(null);
        }}
        rental={selectedRentalForPartner}
        onAssign={handleAssignPartner}
      />
    </>
  );
}

export default Rentals;
